import { useSelector } from 'react-redux';

// Role definitions based on your specifications
const ROLES = {
  ADMIN: 'admin',
  L1_STAFF: 'l1_staff', 
  DC: 'dc',
  DPO: 'dpo',
  SDM: 'sdm',
  ACCOUNTS: 'accounts',
  CITIZEN: 'citizen',
  INSPECTOR: 'inspector'
};

// Role-based access control
const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: {
    // Full admin access - user, role, auth, workflow configs, audit
    canManageUsers: true,
    canManageRoles: true,
    canViewAudit: true,
    canManageWorkflowConfig: true,
    canViewAllApplications: true,
    canRegisterUsers: true,
    canManageAuth: true,
  },
  [ROLES.L1_STAFF]: {
    // Staff operations - apply on citizens behalf, pull, forward, reject application
    canApplyOnBehalf: true,
    canPullApplications: true,
    canForwardApplications: true,
    canRejectApplications: true,
    canViewApplications: true,
    canEditApplications: true,
  },
  [ROLES.DC]: {
    // District Collector operations - forward application
    canForwardApplications: true,
    canViewApplications: true,
    canApproveApplications: true,
    canProcessApplications: true,
  },
  [ROLES.DPO]: {
    // District Project Officer operations - forward, damage_inspection application
    canForwardApplications: true,
    canViewApplications: true,
    canRequestDamageInspection: true,
    canViewInspectionReports: true,
    canProcessApplications: true,
  },
  [ROLES.CITIZEN]: {
    // Citizen operations
    canApplyExgratia: true,
    canViewOwnApplications: true,
    canEditOwnApplications: true,
  },
  [ROLES.INSPECTOR]: {
    // Field Inspector operations
    canViewInspectionReports: true,
    canUploadReports: true,
    canViewPendingReports: true,
  }
};

export const useAuth = () => {
  const { isAuthenticated, isLoading, username, role, permissions, district, subdivision, block } = useSelector((state) => state.auth);
  
  const userRole = role?.name?.toLowerCase() || '';
  const userRoleFullName = role?.full_name || '';
  const userDistrict = district || '';
  const userSubdivision = subdivision || '';
  const userBlock = block || '';

  // Check if user has a specific role
  const hasRole = (targetRole) => {
    return userRole === targetRole.toLowerCase();
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles) => {
    return roles.some(targetRole => hasRole(targetRole));
  };

  // Check if user can perform a specific action based on role
  const canPerformAction = (action) => {
    const rolePermissions = ROLE_PERMISSIONS[userRole];
    return rolePermissions?.[action] || false;
  };

  return {
    isAuthenticated,
    isLoading,
    username,
    role,
    permissions,
    user: { username, role },
    userRole,
    userRoleFullName,
    userDistrict,
    userSubdivision,
    userBlock,
    hasRole,
    hasAnyRole,
    canPerformAction,
    // Convenience methods for common checks
    isAdmin: () => hasRole(ROLES.ADMIN),
    isStaff: () => hasRole(ROLES.L1_STAFF),
    isDC: () => hasRole(ROLES.DC),
    isDPO: () => hasRole(ROLES.DPO),
    isCitizen: () => hasRole(ROLES.CITIZEN),
    isInspector: () => hasRole(ROLES.INSPECTOR),
    // Staff-level access (can handle applications)
    isStaffLevel: () => hasAnyRole([ROLES.L1_STAFF, ROLES.DC, ROLES.DPO, ROLES.ADMIN]),
    // Officer-level access (DC/DPO level)
    isOfficerLevel: () => hasAnyRole([ROLES.DC, ROLES.DPO, ROLES.ADMIN]),
  };
};

// For backward compatibility, create a usePermissions hook that maps to role-based actions
export const usePermissions = () => {
  const { canPerformAction, hasRole, isAdmin, isStaff, isDC, isDPO, isCitizen } = useAuth();

  // Map old permission names to role-based actions
  const hasPermission = (permission) => {
    switch (permission) {
      // Application permissions
      case 'apply_exgratia':
        return canPerformAction('canApplyExgratia') || canPerformAction('canApplyOnBehalf');
      case 'view_applications':
        return canPerformAction('canViewApplications') || canPerformAction('canViewOwnApplications') || canPerformAction('canViewAllApplications');
      case 'edit_application':
      case 'edit_exgratia':
        return canPerformAction('canEditApplications') || canPerformAction('canEditOwnApplications');
      case 'approve_applications':
        return canPerformAction('canApproveApplications');
      case 'reject_applications':
        return canPerformAction('canRejectApplications');
      case 'process_application':
        return canPerformAction('canProcessApplications');
      
      // Admin permissions
      case 'admin_access':
        return isAdmin();
      case 'manage_users':
        return canPerformAction('canManageUsers');
      case 'manage_roles':
        return canPerformAction('canManageRoles');
      case 'view_audit':
        return canPerformAction('canViewAudit');
      case 'manage_workflow':
      case 'manage_workflow_configs':
        return canPerformAction('canManageWorkflowConfig');
      
      // General permissions
      case 'view_dashboard':
        return true; // All authenticated users can view dashboard
      case 'view_profile':
        return true; // All authenticated users can view profile
      case 'view_reports':
        return !isCitizen(); // All except citizens can view reports
      
      // Workflow permissions
      case 'forward_applications':
        return canPerformAction('canForwardApplications');
      case 'pull_applications':
        return canPerformAction('canPullApplications');
      
      // Inspection permissions  
      case 'view_inspection_reports':
        return canPerformAction('canViewInspectionReports');
      case 'upload_inspection_reports':
        return canPerformAction('canUploadReports');
      case 'damage_inspection':
        return canPerformAction('canRequestDamageInspection');
        
      default:
        return false;
    }
  };

  const hasAnyPermission = (permissions) => {
    return permissions.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = (requiredPermissions) => {
    return requiredPermissions.every(permission => hasPermission(permission));
  };

  const checkRole = (requiredRole) => {
    return hasRole(requiredRole);
  };

  const checkAnyRole = (requiredRoles) => {
    return requiredRoles.some(role => hasRole(role));
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole: checkRole,
    hasAnyRole: checkAnyRole,
    isAdmin: isAdmin(),
    isCitizen: isCitizen(),
    isStaff: isStaff(),
    isOfficer: isDC() || isDPO(),
    isInspector: () => hasRole('inspector'),
    isAccounts: () => hasRole('accounts'),
  };
};
